import React, { useEffect, useRef, useState } from "react";
import "./ZynvertEdge.css";

/*
 * ZynvertEdge — "The Zynvert Edge" section
 *
 * Layout: full-bleed dark section with a bold headline, then a
 * horizontal 4-card "commitment grid" where each card has a large
 * outlined number, icon, title, and description. Below that, a
 * full-width "trust bar" with 3 bold stat callouts separated by
 * vertical dividers. Entrance animation on scroll.
 *
 * Accent: deep electric indigo (#6366f1) — distinct from the
 * green (Battery), blue (Inverter) and amber (BMS) showcases.
 *
 * Namespace: ze-* (zero conflicts with bsv2/inv/bms/pd/bsc)
 */

const CARDS = [
  {
    num: "01",
    icon: "🔬",
    title: "Cell-Level Quality Control",
    body: "Every cell is individually capacity-tested before assembly. We reject anything below rated spec — no exceptions, no shortcuts.",
  },
  {
    num: "02",
    icon: "🛠️",
    title: "Hand-Built in India",
    body: "Each pack is assembled by trained technicians, not a conveyor belt. Terminals are torqued, welds are inspected, BMS is bench-tested.",
  },
  {
    num: "03",
    icon: "🧪",
    title: "Burn-In & Load Testing",
    body: "Before it ships, every unit completes a full charge-discharge cycle under load. Your first use is not its first use.",
  },
  {
    num: "04",
    icon: "📦",
    title: "Delivered Ready to Run",
    body: "Shipped at 50% SOC for safe transport. Terminals pre-fitted, wires labelled, documentation included. Unbox and connect.",
  },
];

const STATS = [
  { value: "100%", label: "Cells individually tested" },
  { value: "48h",  label: "Burn-in before dispatch" },
  { value: "2×",   label: "Quality checks per unit" },
];

const ZynvertEdge = () => {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className={`ze-root${visible ? " ze-visible" : ""}`}
      ref={rootRef}
    >
      {/* BG layers */}
      <div className="ze-bg">
        <div className="ze-bg-dots" />
        <div className="ze-bg-fade" />
        {/* Diagonal accent stripe */}
        <div className="ze-bg-stripe" />
      </div>

      {/* Header block */}
      <div className="ze-header">
        <div className="ze-eyebrow">
          <span className="ze-eyebrow-dot" />
          THE ZYNVERT EDGE
        </div>
        <h2 className="ze-headline">
          Every unit. <em>Every time.</em>
        </h2>
        <p className="ze-subhead">
          Our commitment isn't a marketing line — it's a process.
          Here's what happens before your battery reaches your door.
        </p>
      </div>

      {/* Cards grid */}
      <div className="ze-cards">
        {CARDS.map((card, i) => (
          <div
            className="ze-card"
            key={card.num}
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            {/* Large outlined number — purely decorative */}
            <span className="ze-card-num">{card.num}</span>

            {/* Icon */}
            <span className="ze-card-icon">{card.icon}</span>

            {/* Content */}
            <h3 className="ze-card-title">{card.title}</h3>
            <p className="ze-card-body">{card.body}</p>

            {/* Bottom accent line — animates in */}
            <div className="ze-card-line" />
          </div>
        ))}
      </div>

      {/* Connector line between cards (decorative, desktop only) */}
      <div className="ze-connector" aria-hidden="true">
        <div className="ze-connector-track">
          <div className="ze-connector-fill" />
        </div>
        {CARDS.map((_, i) => (
          <div
            key={i}
            className="ze-connector-node"
            style={{ left: `${(i / (CARDS.length - 1)) * 100}%` }}
          />
        ))}
      </div>

      {/* Trust / stat bar */}
      <div className="ze-stats">
        {STATS.map((s, i) => (
          <React.Fragment key={s.label}>
            <div className="ze-stat">
              <span className="ze-stat-value">{s.value}</span>
              <span className="ze-stat-label">{s.label}</span>
            </div>
            {i < STATS.length - 1 && <div className="ze-stat-divider" />}
          </React.Fragment>
        ))}
      </div>

      {/* Bottom tagline */}
      <p className="ze-tagline">
        Built in India. Backed by Zynvert. Trusted by thousands of homes.*
      </p>
    </section>
  );
};

export default ZynvertEdge;